type Blip = { a: number; r: number; born: number; life: number };

export function startSonar(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext("2d", { alpha: true });
  if (!ctx) return () => undefined;

  const blips: Blip[] = [];
  let sweep = 0;
  let raf = 0;
  let last = performance.now();
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const resize = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  const spawn = () => {
    blips.push({
      a: Math.random() * Math.PI * 2,
      r: 0.18 + Math.random() * 0.72,
      born: performance.now(),
      life: 2800 + Math.random() * 2400,
    });
    if (blips.length > 18) blips.shift();
  };

  const draw = (now: number) => {
    const style = getComputedStyle(document.documentElement);
    const hi = style.getPropertyValue("--sonar-hi").trim() || "125, 255, 178";
    const cyan = style.getPropertyValue("--sonar-cyan").trim() || "158, 231, 255";

    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    const cx = w * 0.5;
    const cy = h * 0.58;
    const maxR = Math.hypot(w, h) * 0.42;
    const dt = Math.min(40, now - last);
    last = now;
    if (!reduce) sweep = (sweep + dt * 0.00042) % (Math.PI * 2);

    ctx.clearRect(0, 0, w, h);

    const g = ctx.createRadialGradient(cx, cy, 20, cx, cy, maxR * 1.15);
    g.addColorStop(0, "rgba(8, 28, 18, 0.35)");
    g.addColorStop(0.55, "rgba(3, 8, 6, 0.12)");
    g.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    ctx.save();
    ctx.translate(cx, cy);
    ctx.strokeStyle = `rgba(${hi}, 0.07)`;
    ctx.lineWidth = 1;
    for (let i = 1; i <= 6; i++) {
      ctx.beginPath();
      ctx.arc(0, 0, (maxR / 6) * i, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.moveTo(-maxR, 0);
    ctx.lineTo(maxR, 0);
    ctx.moveTo(0, -maxR);
    ctx.lineTo(0, maxR);
    ctx.stroke();

    ctx.rotate(sweep);
    const sg = ctx.createLinearGradient(0, 0, maxR, 0);
    sg.addColorStop(0, `rgba(${hi}, 0.0)`);
    sg.addColorStop(0.7, `rgba(${hi}, 0.04)`);
    sg.addColorStop(1, `rgba(${cyan}, 0.22)`);
    ctx.fillStyle = sg;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, maxR, -0.55, 0.02);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    for (const b of blips) {
      const age = now - b.born;
      const t = age / b.life;
      if (t >= 1) continue;
      const x = cx + Math.cos(b.a) * b.r * maxR;
      const y = cy + Math.sin(b.a) * b.r * maxR;
      const alpha = t < 0.12 ? t / 0.12 : 1 - (t - 0.12) / 0.88;
      ctx.beginPath();
      ctx.arc(x, y, 2.2, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${hi}, ${0.55 * alpha})`;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x, y, 10 + t * 18, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(${cyan}, ${0.22 * alpha})`;
      ctx.stroke();
    }

    raf = requestAnimationFrame(draw);
  };

  resize();
  spawn();
  const onResize = () => resize();
  window.addEventListener("resize", onResize);
  const spawnId = window.setInterval(spawn, reduce ? 4000 : 1600);
  raf = requestAnimationFrame(draw);

  return () => {
    cancelAnimationFrame(raf);
    window.clearInterval(spawnId);
    window.removeEventListener("resize", onResize);
  };
}
